import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, Loader2, Trash2, Search, ChevronLeft, Mic, Check, Tag, X, ImagePlus, Sparkles, RefreshCw, Calendar } from "lucide-react";
import { listMemories, getAudioUrl, deleteMemory, updateMemory, patchMemoryMetadata, updateMemoryEventDate } from "../services/memories";
import { uploadMemoryImage, deleteMediaFile, getMediaUrl } from "../services/media";
import { generatePolish } from "../services/polish";
import { getSettings } from "../services/settings";
import { useT } from "../services/i18n";

const L = {
  fr: { search: "Rechercher une pensée…", today: "Aujourd'hui", yesterday: "Hier", noResults: "Aucun résultat.", undated: "À situer", listen: "Écouter", dateBtn: "Dater ce souvenir", dateEdit: "Modifier la date", dateYear: "Année", dateMonth: "Mois (facultatif)", dateSaveBtn: "Enregistrer la date", dateClear: "Retirer la date", capturedOn: "Capté le", editPlaceholder: "Écris ton souvenir…", tags: "Tags", tagPlaceholder: "Ajouter un tag…", save: "Enregistrer", saved: "Enregistré", errSave: "L'enregistrement a échoué.", filteredBy: "Filtré par", clearFilter: "Retirer", photos: "Photos", addPhoto: "Ajouter une photo", errPhoto: "L'ajout de la photo a échoué.", delPhotoTitle: "Effacer cette photo ?", delPhotoText: "La photo sera supprimée définitivement.", close: "Fermer", polishClean: "Au propre", polishOriginal: "Original", polishCta: "Mettre au propre", polishing: "Mise au propre…", polishRegen: "Régénérer", polishDelete: "Supprimer la version au propre", errPolish: "La mise au propre a échoué." },
  en: { search: "Search a thought…", today: "Today", yesterday: "Yesterday", noResults: "No results.", undated: "To place", listen: "Listen", dateBtn: "Date this memory", dateEdit: "Edit date", dateYear: "Year", dateMonth: "Month (optional)", dateSaveBtn: "Save date", dateClear: "Remove date", capturedOn: "Captured on", editPlaceholder: "Write your memory…", tags: "Tags", tagPlaceholder: "Add a tag…", save: "Save", saved: "Saved", errSave: "Saving failed.", filteredBy: "Filtered by", clearFilter: "Clear", photos: "Photos", addPhoto: "Add a photo", errPhoto: "Adding the photo failed.", delPhotoTitle: "Delete this photo?", delPhotoText: "The photo will be permanently deleted.", close: "Close", polishClean: "Polished", polishOriginal: "Original", polishCta: "Tidy up", polishing: "Tidying up…", polishRegen: "Regenerate", polishDelete: "Delete the polished version", errPolish: "Tidying up failed." },
  ru: { search: "Искать мысль…", today: "Сегодня", yesterday: "Вчера", noResults: "Ничего не найдено.", undated: "Без даты", listen: "Слушать", dateBtn: "Указать дату", dateEdit: "Изменить дату", dateYear: "Год", dateMonth: "Месяц (необязательно)", dateSaveBtn: "Сохранить дату", dateClear: "Убрать дату", capturedOn: "Записано", editPlaceholder: "Запишите воспоминание…", tags: "Теги", tagPlaceholder: "Добавить тег…", save: "Сохранить", saved: "Сохранено", errSave: "Не удалось сохранить.", filteredBy: "Фильтр по", clearFilter: "Сбросить", photos: "Фото", addPhoto: "Добавить фото", errPhoto: "Не удалось добавить фото.", delPhotoTitle: "Удалить это фото?", delPhotoText: "Фото будет удалено безвозвратно.", close: "Закрыть", polishClean: "Начисто", polishOriginal: "Оригинал", polishCta: "Привести в порядок", polishing: "Обработка…", polishRegen: "Заново", polishDelete: "Удалить улучшенную версию", errPolish: "Не удалось обработать." },
};

export default function ExploreScreen() {
  const { t, lang } = useT();
  const lt = (k) => (L[lang] || L.fr)[k] || L.fr[k];
  const locale = lang === "fr" ? "fr-FR" : lang === "ru" ? "ru-RU" : "en-US";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [selected, setSelected] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const audioRef = useRef(null);

  // Édition (texte + tags)
  const [editText, setEditText] = useState("");
  const [editTags, setEditTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Photos
  const [imgUrls, setImgUrls] = useState({});
  const [imgUploading, setImgUploading] = useState(false);
  const [imgConfirm, setImgConfirm] = useState(null);
  const [viewer, setViewer] = useState(null);
  const fileInputRef = useRef(null);

  // Embellissement
  const [polishView, setPolishView] = useState(false); // true = "Au propre", false = "Original"
  const [polishing, setPolishing] = useState(false);

  // Datation manuelle
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dpYear, setDpYear] = useState("");
  const [dpMonth, setDpMonth] = useState("");

  useEffect(() => {
    let mounted = true;
    listMemories()
      .then((data) => mounted && setItems(data))
      .catch(() => mounted && setError(t("exp_err_load")))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
      if (audioRef.current) audioRef.current.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => {
      const matchesQuery = !q || (m.transcript || "").toLowerCase().includes(q);
      const tags = Array.isArray(m.metadata?.tags) ? m.metadata.tags : [];
      const matchesTag = !activeTag || tags.some((x) => x.toLowerCase() === activeTag.toLowerCase());
      return matchesQuery && matchesTag;
    });
  }, [items, query, activeTag]);

  const groups = useMemo(() => groupByYear(filtered, lt, locale), [filtered, lang]); // eslint-disable-line

  const stopAudio = () => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
  };

  const togglePlay = async (m) => {
    if (playingId === m.id) {
      stopAudio();
      return;
    }
    try {
      setLoadingId(m.id);
      const url = await getAudioUrl(m.audio_path);
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlayingId(null);
      await audioRef.current.play();
      setPlayingId(m.id);
    } catch (e) {
      setError(t("exp_err_audio"));
    } finally {
      setLoadingId(null);
    }
  };

  const confirmDelete = async () => {
    const m = confirm;
    if (!m) return;
    setDeleting(true);
    const previous = items;
    setItems((list) => list.filter((x) => x.id !== m.id));
    if (playingId === m.id) stopAudio();
    try {
      await deleteMemory(m.id);
      setConfirm(null);
      setSelected(null);
    } catch (e) {
      setItems(previous);
      setError(t("exp_err_delete"));
      setConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = (m) => {
    stopAudio();
    setSelected(m);
    setEditText(m.transcript || "");
    setEditTags(Array.isArray(m.metadata?.tags) ? m.metadata.tags : []);
    setTagInput("");
    setSavedFlash(false);
    setError("");
  };

  const closeDetail = () => {
    stopAudio();
    setSelected(null);
    setTagInput("");
  };

  const addTag = (raw) => {
    const tag = (raw || "").trim().replace(/^#/, "");
    if (!tag) return;
    if (editTags.some((x) => x.toLowerCase() === tag.toLowerCase())) {
      setTagInput("");
      return;
    }
    setEditTags((prev) => [...prev, tag]);
    setTagInput("");
  };

  const removeTag = (tag) => setEditTags((prev) => prev.filter((x) => x !== tag));

  const dirty = useMemo(() => {
    if (!selected) return false;
    const origTags = Array.isArray(selected.metadata?.tags) ? selected.metadata.tags : [];
    return (
      editText !== (selected.transcript || "") ||
      JSON.stringify(editTags) !== JSON.stringify(origTags)
    );
  }, [selected, editText, editTags]);

  const handleSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateMemory(selected.id, {
        transcript: editText,
        tags: editTags,
        metadata: selected.metadata,
      });
      setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      setSelected((s) => ({ ...s, ...updated }));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      setError(lt("errSave"));
    } finally {
      setSaving(false);
    }
  };

  // Charge les URLs signées des photos du souvenir ouvert.
  useEffect(() => {
    if (!selected) return;
    const imgs = Array.isArray(selected.metadata?.images) ? selected.metadata.images : [];
    imgs.forEach((p) => {
      setImgUrls((cur) => {
        if (cur[p]) return cur;
        getMediaUrl(p)
          .then((u) => setImgUrls((m) => ({ ...m, [p]: u })))
          .catch(() => {});
        return cur;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const currentImages = () =>
    Array.isArray(selected?.metadata?.images) ? selected.metadata.images : [];

  const persistImages = async (images) => {
    const updated = await patchMemoryMetadata(selected.id, selected.metadata, { images });
    setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setSelected((s) => ({ ...s, ...updated }));
  };

  const handleAddPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length || imgUploading) return;
    setImgUploading(true);
    setError("");
    try {
      const newPaths = [];
      for (const f of files) {
        const path = await uploadMemoryImage(f);
        newPaths.push(path);
        getMediaUrl(path)
          .then((u) => setImgUrls((m) => ({ ...m, [path]: u })))
          .catch(() => {});
      }
      await persistImages([...currentImages(), ...newPaths]);
    } catch (e) {
      setError(lt("errPhoto"));
    } finally {
      setImgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const confirmDeleteImage = async () => {
    const path = imgConfirm;
    if (!path) return;
    try {
      await deleteMediaFile(path);
      await persistImages(currentImages().filter((p) => p !== path));
      setImgConfirm(null);
    } catch (e) {
      setError(lt("errPhoto"));
      setImgConfirm(null);
    }
  };

  const runPolish = async () => {
    if (!selected || polishing) return;
    const src = (selected.transcript || "").trim();
    if (!src) return;
    setPolishing(true);
    setError("");
    try {
      const polished = await generatePolish(src);
      const updated = await patchMemoryMetadata(selected.id, selected.metadata, { polished });
      setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      setSelected((s) => ({ ...s, ...updated }));
      setPolishView(true);
    } catch (e) {
      setError(lt("errPolish"));
    } finally {
      setPolishing(false);
    }
  };

  const deletePolish = async () => {
    if (!selected) return;
    const { polished, ...rest } = selected.metadata || {};
    try {
      const updated = await patchMemoryMetadata(selected.id, {}, rest);
      setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      setSelected((s) => ({ ...s, ...updated }));
      setPolishView(false);
    } catch (e) {
      setError(lt("errPolish"));
    }
  };

  const openDatePicker = () => {
    const p = eventParts(selected?.event_date);
    setDpYear(p ? String(p.y) : "");
    setDpMonth(p && !(p.m === 1 && p.d === 1) ? String(p.m) : "");
    setDatePickerOpen(true);
  };

  const persistEventDate = async (eventDate) => {
    try {
      const updated = await updateMemoryEventDate(selected.id, eventDate);
      setItems((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      setSelected((s) => ({ ...s, ...updated }));
      setDatePickerOpen(false);
    } catch (e) {
      setError(lt("errSave"));
    }
  };

  const saveEventDate = () => {
    const y = parseInt(dpYear, 10);
    if (!Number.isFinite(y) || y < 1900 || y > new Date().getFullYear()) return;
    const m = parseInt(dpMonth, 10);
    const mm = Number.isFinite(m) && m >= 1 && m <= 12 ? String(m).padStart(2, "0") : "01";
    persistEventDate(`${y}-${mm}-01`);
  };


  // À l'ouverture : si "Embellir" ON et pas encore de version au propre, générer en arrière-plan.
  useEffect(() => {
    if (!selected) return;
    const hasPolished = !!selected.metadata?.polished;
    setPolishView(hasPolished);
    if (!hasPolished && getSettings().embellish && (selected.transcript || "").trim()) {
      runPolish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  return (
    <div className="min-h-[100dvh]" data-testid="explore-screen">
      <header className="px-6 pt-[max(20px,env(safe-area-inset-top))] pb-3">
        <h1 className="brand text-2xl font-bold text-[var(--rmr-text)]">{t("exp_title")}</h1>
        <p className="text-sm text-[var(--rmr-text-2)]">{t("exp_subtitle")}</p>
      </header>

      {/* Recherche */}
      {!loading && !error && items.length > 0 && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 rounded-full border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-2.5">
            <Search size={18} className="shrink-0 text-[var(--rmr-text-3)]" />
            <input
              data-testid="explore-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lt("search")}
              className="w-full bg-transparent text-[15px] text-[var(--rmr-text)] outline-none placeholder:text-[var(--rmr-text-3)]"
            />
          </div>
          {activeTag && (
            <div className="mt-2 flex items-center justify-between" data-testid="tag-filter-banner">
              <span className="inline-flex items-center gap-1.5 text-sm text-[var(--rmr-text-2)]">
                {lt("filteredBy")}
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--rmr-accent)] px-2.5 py-0.5 text-xs font-semibold text-white">
                  #{activeTag}
                </span>
              </span>
              <button
                data-testid="clear-tag-filter-btn"
                onClick={() => setActiveTag(null)}
                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-[var(--rmr-accent)] transition-colors hover:bg-[var(--rmr-chip)]"
              >
                <X size={13} /> {lt("clearFilter")}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="px-4 pb-[calc(96px+env(safe-area-inset-bottom))]">
        {loading && (
          <p className="px-2 py-8 text-center text-[var(--rmr-text-2)]" data-testid="explore-loading">
            {t("exp_loading")}
          </p>
        )}
        {error && (
          <p className="px-2 py-8 text-center text-[var(--rmr-danger)]" data-testid="explore-error">
            {error}
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="px-2 py-12 text-center text-[var(--rmr-text-2)]" data-testid="explore-empty">
            {t("exp_empty")}
          </p>
        )}
        {!loading && !error && items.length > 0 && filtered.length === 0 && (
          <p className="px-2 py-12 text-center text-[var(--rmr-text-2)]" data-testid="explore-no-results">
            {lt("noResults")}
          </p>
        )}

        <div data-testid="memory-list" className="space-y-6">
          {groups.map((g) => (
            <section key={g.key}>
              <h2 className="mb-3 px-1 text-xs font-bold uppercase tracking-wider text-[var(--rmr-text-3)]">
                {g.label}
              </h2>
              <ol className="relative ml-1 space-y-3 border-l border-[var(--rmr-border)] pl-5">
                {g.items.map((m, i) => (
                  <li key={m.id} className="relative">
                    <span
                      className="absolute -left-[26px] top-4 h-3 w-3 rounded-full bg-[var(--rmr-accent)] ring-4 ring-[var(--rmr-bg)]"
                      aria-hidden="true"
                    />
                    <motion.button
                      data-testid="memory-item"
                      onClick={() => openDetail(m)}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.2) }}
                      className="block w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-4 text-left transition-transform active:scale-[0.99]"
                    >
                      <p className="line-clamp-3 whitespace-pre-wrap text-[15px] leading-relaxed text-[var(--rmr-text)]">
                        {m.transcript || (
                          <span className="italic text-[var(--rmr-text-3)]">{t("exp_audio_no_text")}</span>
                        )}
                      </p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-[var(--rmr-text-2)]">
                        {m.event_date && <span data-testid="memory-event-date">{formatEventDate(m.event_date, locale)}</span>}
                        {m.audio_path && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--rmr-chip)] px-2 py-0.5 text-[var(--rmr-accent)]">
                            <Mic size={11} /> {lt("listen")}
                          </span>
                        )}
                        {m.metadata?.polished && (
                          <span
                            data-testid="memory-polished-badge"
                            title={lt("polishClean")}
                            className="inline-flex items-center gap-1 rounded-full bg-[var(--rmr-chip)] px-2 py-0.5 text-[var(--rmr-accent)]"
                          >
                            <Sparkles size={11} /> {lt("polishClean")}
                          </span>
                        )}
                      </div>
                      {Array.isArray(m.metadata?.tags) && m.metadata.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="memory-item-tags">
                          {m.metadata.tags.slice(0, 4).map((tag) => (
                            <span
                              key={tag}
                              role="button"
                              tabIndex={0}
                              data-testid="memory-tag-chip"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTag(tag);
                                setQuery("");
                              }}
                              className="cursor-pointer rounded-full bg-[var(--rmr-chip)] px-2 py-0.5 text-[11px] font-medium text-[var(--rmr-accent)] transition-colors hover:bg-[var(--rmr-accent)] hover:text-white"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </motion.button>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      </div>

      {/* Vue détail plein écran */}
      <AnimatePresence>
        {selected && (
          <motion.div
            key="detail"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 z-40 mx-auto flex max-w-md flex-col bg-[var(--rmr-bg)]"
            data-testid="memory-detail"
          >
            <header className="flex items-center justify-between px-4 pt-[max(20px,env(safe-area-inset-top))] pb-2">
              <button
                data-testid="detail-back-btn"
                onClick={closeDetail}
                className="grid h-10 w-10 place-items-center rounded-full text-[var(--rmr-text-2)] transition-colors hover:bg-[var(--rmr-surface)] hover:text-[var(--rmr-accent)]"
                aria-label={t("set_back_aria")}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                data-testid="detail-delete-btn"
                onClick={() => setConfirm(selected)}
                className="grid h-10 w-10 place-items-center rounded-full text-[var(--rmr-text-3)] transition-colors hover:bg-[var(--rmr-danger-bg)] hover:text-[var(--rmr-danger)]"
                aria-label={t("exp_delete_aria")}
              >
                <Trash2 size={20} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 pb-32">
              <div className="mb-6" data-testid="detail-date-block">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--rmr-accent)]" data-testid="detail-event-date">
                    {selected.event_date ? formatEventDate(selected.event_date, locale) : lt("undated")}
                  </span>
                  <button
                    data-testid="date-edit-btn"
                    onClick={openDatePicker}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--rmr-chip)] px-2.5 py-1 text-xs font-semibold text-[var(--rmr-accent)] transition-transform active:scale-95"
                  >
                    <Calendar size={12} /> {selected.event_date ? lt("dateEdit") : lt("dateBtn")}
                  </button>
                </div>
                <p className="brand mt-1 text-xs text-[var(--rmr-text-3)]">
                  {lt("capturedOn")} {formatFullDate(selected.created_at, locale)}
                </p>

                {datePickerOpen && (
                  <div className="mt-3 rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-3" data-testid="date-picker">
                    <div className="flex gap-2">
                      <input
                        data-testid="date-year-input"
                        value={dpYear}
                        onChange={(e) => setDpYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                        inputMode="numeric"
                        placeholder={lt("dateYear")}
                        className="w-24 rounded-xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-3 py-2 text-sm text-[var(--rmr-text)] outline-none focus:border-[var(--rmr-accent)]"
                      />
                      <select
                        data-testid="date-month-select"
                        value={dpMonth}
                        onChange={(e) => setDpMonth(e.target.value)}
                        className="flex-1 rounded-xl border border-[var(--rmr-border)] bg-[var(--rmr-chip)] px-3 py-2 text-sm text-[var(--rmr-text)] outline-none focus:border-[var(--rmr-accent)]"
                      >
                        <option value="">{lt("dateMonth")}</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2000, i, 1).toLocaleDateString(locale, { month: "long" })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        data-testid="date-save-btn"
                        onClick={saveEventDate}
                        disabled={!dpYear}
                        className="flex-1 rounded-xl bg-[var(--rmr-accent)] py-2 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
                      >
                        {lt("dateSaveBtn")}
                      </button>
                      {selected.event_date && (
                        <button
                          data-testid="date-clear-btn"
                          onClick={() => persistEventDate(null)}
                          className="rounded-xl px-3 py-2 text-sm font-semibold text-[var(--rmr-text-2)] transition-colors hover:text-[var(--rmr-danger)]"
                        >
                          {lt("dateClear")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Toggle Au propre / Original (si version au propre existe) */}
              {selected.metadata?.polished && (
                <div className="mb-4 inline-flex gap-1 rounded-2xl bg-[var(--rmr-chip)] p-1" data-testid="polish-toggle">
                  <button
                    data-testid="polish-view-clean"
                    onClick={() => setPolishView(true)}
                    className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors ${
                      polishView ? "bg-[var(--rmr-surface)] text-[var(--rmr-accent)] shadow-sm" : "text-[var(--rmr-text-2)]"
                    }`}
                  >
                    <Sparkles size={14} /> {lt("polishClean")}
                  </button>
                  <button
                    data-testid="polish-view-original"
                    onClick={() => setPolishView(false)}
                    className={`rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors ${
                      !polishView ? "bg-[var(--rmr-surface)] text-[var(--rmr-accent)] shadow-sm" : "text-[var(--rmr-text-2)]"
                    }`}
                  >
                    {lt("polishOriginal")}
                  </button>
                </div>
              )}

              {selected.metadata?.polished && polishView ? (
                <p
                  data-testid="detail-polished-text"
                  className="whitespace-pre-wrap break-words text-lg leading-relaxed text-[var(--rmr-text)]"
                >
                  {selected.metadata.polished}
                </p>
              ) : (
                <textarea
                  data-testid="detail-text-input"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder={lt("editPlaceholder")}
                  rows={1}
                  ref={(el) => {
                    if (el) {
                      el.style.height = "auto";
                      el.style.height = el.scrollHeight + "px";
                    }
                  }}
                  className="w-full resize-none whitespace-pre-wrap break-words bg-transparent text-lg leading-relaxed text-[var(--rmr-text)] outline-none placeholder:text-[var(--rmr-text-3)]"
                />
              )}

              {/* Actions embellissement */}
              {(selected.transcript || "").trim() && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {!selected.metadata?.polished ? (
                    <button
                      data-testid="polish-cta-btn"
                      onClick={runPolish}
                      disabled={polishing}
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--rmr-chip)] px-4 py-2 text-sm font-semibold text-[var(--rmr-accent)] transition-transform active:scale-[0.98] disabled:opacity-60"
                    >
                      {polishing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                      {polishing ? lt("polishing") : lt("polishCta")}
                    </button>
                  ) : (
                    <>
                      <button
                        data-testid="polish-regen-btn"
                        onClick={runPolish}
                        disabled={polishing}
                        className="inline-flex items-center gap-2 rounded-full bg-[var(--rmr-chip)] px-4 py-2 text-sm font-semibold text-[var(--rmr-accent)] transition-transform active:scale-[0.98] disabled:opacity-60"
                      >
                        {polishing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                        {polishing ? lt("polishing") : lt("polishRegen")}
                      </button>
                      <button
                        data-testid="polish-delete-btn"
                        onClick={deletePolish}
                        disabled={polishing}
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-[var(--rmr-text-2)] transition-colors hover:text-[var(--rmr-danger)] disabled:opacity-60"
                      >
                        <X size={15} /> {lt("polishDelete")}
                      </button>
                    </>
                  )}
                </div>
              )}

              {selected.audio_path && (
                <button
                  data-testid="detail-play-btn"
                  onClick={() => togglePlay(selected)}
                  className="mt-6 flex items-center gap-3 rounded-full bg-[var(--rmr-chip)] py-3 pl-3 pr-6 text-sm font-semibold text-[var(--rmr-accent)] transition-transform active:scale-[0.98]"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--rmr-accent)] text-white">
                    {loadingId === selected.id ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : playingId === selected.id ? (
                      <Pause size={18} />
                    ) : (
                      <Play size={18} />
                    )}
                  </span>
                  {lt("listen")}
                </button>
              )}

              {/* Tags */}
              <div className="mt-8">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--rmr-text-3)]">
                  <Tag size={13} /> {lt("tags")}
                </p>
                <div className="flex flex-wrap items-center gap-2" data-testid="detail-tags">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      data-testid="tag-chip"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rmr-chip)] py-1.5 pl-3 pr-2 text-sm font-medium text-[var(--rmr-accent)]"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        data-testid="tag-remove-btn"
                        className="grid h-4 w-4 place-items-center rounded-full text-[var(--rmr-accent)] transition-colors hover:bg-[var(--rmr-accent)] hover:text-white"
                        aria-label={`remove ${tag}`}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  <input
                    data-testid="tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag(tagInput);
                      } else if (e.key === "Backspace" && !tagInput && editTags.length) {
                        removeTag(editTags[editTags.length - 1]);
                      }
                    }}
                    onBlur={() => addTag(tagInput)}
                    placeholder={lt("tagPlaceholder")}
                    className="min-w-[120px] flex-1 rounded-full border border-dashed border-[var(--rmr-border)] bg-transparent px-3 py-1.5 text-sm text-[var(--rmr-text)] outline-none placeholder:text-[var(--rmr-text-3)] focus:border-[var(--rmr-accent)]"
                  />
                </div>
              </div>

              {/* Photos */}
              <div className="mt-8">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--rmr-text-3)]">
                  <ImagePlus size={13} /> {lt("photos")}
                </p>
                <div className="grid grid-cols-3 gap-2" data-testid="detail-photos">
                  {currentImages().map((path) => (
                    <div key={path} className="relative aspect-square overflow-hidden rounded-2xl bg-[var(--rmr-chip)]">
                      {imgUrls[path] ? (
                        <button
                          data-testid="photo-thumb"
                          onClick={() => setViewer(imgUrls[path])}
                          className="h-full w-full"
                        >
                          <img src={imgUrls[path]} alt="" className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <div className="grid h-full w-full place-items-center">
                          <Loader2 size={18} className="animate-spin text-[var(--rmr-text-3)]" />
                        </div>
                      )}
                      <button
                        data-testid="photo-delete-btn"
                        onClick={() => setImgConfirm(path)}
                        className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-transform active:scale-90"
                        aria-label="delete photo"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}

                  <button
                    data-testid="add-photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imgUploading}
                    className="grid aspect-square place-items-center gap-1 rounded-2xl border border-dashed border-[var(--rmr-border)] text-[var(--rmr-text-3)] transition-colors hover:border-[var(--rmr-accent)] hover:text-[var(--rmr-accent)] disabled:opacity-60"
                  >
                    {imgUploading ? (
                      <Loader2 size={22} className="animate-spin" />
                    ) : (
                      <>
                        <ImagePlus size={22} />
                        <span className="px-1 text-center text-[11px] font-medium leading-tight">{lt("addPhoto")}</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    data-testid="photo-file-input"
                    onChange={(e) => handleAddPhotos(e.target.files)}
                    className="hidden"
                  />
                </div>
              </div>

              {error && (
                <p className="mt-4 text-sm text-[var(--rmr-danger)]" data-testid="detail-error">
                  {error}
                </p>
              )}
            </div>

            {/* Barre Enregistrer */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[var(--rmr-bg)] via-[var(--rmr-bg)] to-transparent px-6 pb-[max(20px,env(safe-area-inset-bottom))] pt-6">
              <button
                data-testid="detail-save-btn"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-accent)] py-3.5 text-[15px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {saving ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : savedFlash ? (
                  <Check size={18} />
                ) : null}
                {savedFlash ? lt("saved") : lt("save")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation de suppression */}
      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
          data-testid="delete-confirm-modal"
          onClick={() => !deleting && setConfirm(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="brand text-xl font-bold text-[var(--rmr-text)]">{t("exp_del_title")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--rmr-text-2)]">{t("exp_del_text")}</p>
            <div className="mt-5 flex gap-2">
              <button
                data-testid="delete-cancel-btn"
                onClick={() => setConfirm(null)}
                disabled={deleting}
                className="flex-1 rounded-2xl bg-[var(--rmr-chip)] py-3 text-sm font-semibold text-[var(--rmr-text-2)] transition-transform active:scale-[0.98] disabled:opacity-60"
              >
                {t("exp_cancel")}
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleting}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-[var(--rmr-danger)] py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                {t("exp_delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse photo plein écran */}
      {viewer && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          data-testid="photo-viewer"
          onClick={() => setViewer(null)}
        >
          <button
            data-testid="photo-viewer-close"
            onClick={() => setViewer(null)}
            className="absolute right-4 top-[max(16px,env(safe-area-inset-top))] grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white backdrop-blur-sm"
            aria-label={lt("close")}
          >
            <X size={22} />
          </button>
          <img
            src={viewer}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        </div>
      )}

      {/* Confirmation suppression photo */}
      {imgConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
          data-testid="photo-delete-modal"
          onClick={() => setImgConfirm(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="brand text-xl font-bold text-[var(--rmr-text)]">{lt("delPhotoTitle")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[var(--rmr-text-2)]">{lt("delPhotoText")}</p>
            <div className="mt-5 flex gap-2">
              <button
                data-testid="photo-delete-cancel"
                onClick={() => setImgConfirm(null)}
                className="flex-1 rounded-2xl bg-[var(--rmr-chip)] py-3 text-sm font-semibold text-[var(--rmr-text-2)] transition-transform active:scale-[0.98]"
              >
                {t("exp_cancel")}
              </button>
              <button
                data-testid="photo-delete-confirm"
                onClick={confirmDeleteImage}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-[var(--rmr-danger)] py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]"
              >
                <Trash2 size={16} /> {t("exp_delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function groupByYear(items, lt, locale) {
  const undated = [];
  const byYear = {};
  for (const m of items) {
    const p = eventParts(m.event_date);
    if (!p) {
      undated.push(m);
      continue;
    }
    (byYear[p.y] = byYear[p.y] || []).push(m);
  }
  const groups = [];
  if (undated.length) {
    undated.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    groups.push({ key: "undated", label: lt("undated"), items: undated });
  }
  Object.keys(byYear)
    .map(Number)
    .sort((a, b) => b - a)
    .forEach((y) => {
      const arr = byYear[y].sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
      groups.push({ key: String(y), label: String(y), items: arr });
    });
  return groups;
}

// Parse la date du souvenir sans décalage de fuseau (composants directs).
function eventParts(iso) {
  if (!iso) return null;
  const datePart = String(iso).split("T")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y) return null;
  return { y, m: m || 1, d: d || 1 };
}

// Repère de date lisible : année seule si jour/mois inconnus (1er janvier), sinon mois + année.
function formatEventDate(iso, locale) {
  const p = eventParts(iso);
  if (!p) return "";
  if (p.m === 1 && p.d === 1) return String(p.y);
  try {
    return new Date(p.y, p.m - 1, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  } catch {
    return String(p.y);
  }
}

function formatTime(iso, locale) {
  try {
    return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatFullDate(iso, locale) {
  try {
    return new Date(iso).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}
